<template>
  <div>
    <div class="table-header d-flex">
      <v-text-field
        class=""
        block
        v-model="search"
        rounded
        outlined
        dense
        clearable
        prepend-inner-icon="mdi-magnify"
        :placeholder="$t('search_event')"
      ></v-text-field>
      <v-spacer></v-spacer>
      <v-tooltip v-if="isCalendarEditable" bottom dark color="#333333">
        <template v-slot:activator="{ on }">
          <v-btn
            v-on="on"
            class="mb-1"
            rounded
            color="secondary"
            @click="showAddEvent"
          >
            <v-icon small>mdi-plus</v-icon>
            {{ $t("add_event") }}
          </v-btn>
        </template>
        <span>
          {{ $t("create_event_in_calendar") }}
        </span>
      </v-tooltip>
    </div>
    <v-data-table
      :headers="hotel_events_headers"
      :items="filteredEvents"
      :loading="calendar_store.loading_calendar_events"
      :item-class="getDatatableRowClass"
      :no-data-text="no_calendar_events_text"
      hide-default-footer
      disable-pagination
      @click:row="showEditEvent"
    >
      <template v-slot:loading>
        <v-skeleton-loader type="table-row@10"></v-skeleton-loader>
      </template>
      <template v-slot:[`item.name`]="{ item }">
        <b class="mr-1">{{ item.name }}</b>
        <v-icon x-small v-if="!item.editable" color="grey"
          >mdi-lock-outline</v-icon
        >
        <div class="grey--text caption">
          <v-icon x-small color="grey">
            {{ getEventTypeIcon(item.type) }}
          </v-icon>
          {{ $t(item.type) }}
          <v-icon
            v-for="i in item.duration"
            :key="i"
            color="grey lighten-1"
            x-small
          >
            mdi-circle-outline
          </v-icon>
        </div>
      </template>
      <template v-slot:[`item.type`]="{ item }">
        {{ $t(item.type) }}
      </template>
      <template v-slot:[`item.start_date`]="{ item }">
        {{ item.days }}
      </template>
      <template v-slot:[`item.repeat`]="{ item }">
        <v-chip small color="primary" outlined v-if="item.repeat === 'annual'"
          ><b>{{ $t(item.repeat) }}</b></v-chip
        >
        <v-chip
          small
          color="secondary darken-2"
          outlined
          v-if="item.repeat === 'biannual'"
          ><b>{{ $t(item.repeat) }}</b></v-chip
        >
      </template>
      <template v-slot:[`item.impact`]="{ item }">
        <v-chip dark small :color="getImpactColor(item.impact)"
          ><b>{{ $t("impact_" + item.impact) }}</b></v-chip
        >
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <v-tooltip v-if="isEventEditable(item)" bottom dark color="#333333">
          <template v-slot:activator="{ on }">
            <v-btn
              @click.stop="showMoveEvent(item)"
              icon
              color="secondary"
              class="mr-2"
              v-on="on"
            >
              <v-icon small>mdi-calendar-import-outline</v-icon>
            </v-btn>
          </template>
          <span>
            {{ $t("move_calendar") }}
          </span>
        </v-tooltip>

        <v-tooltip v-if="isEventEditable(item)" bottom dark color="#333333">
          <template v-slot:activator="{ on }">
            <v-btn
              @click.stop="showDeleteEvent(item)"
              icon
              color="secondary"
              v-on="on"
            >
              <v-icon small>mdi-trash-can-outline</v-icon>
            </v-btn>
          </template>
          <span>
            {{ $t("delete") }}
          </span>
        </v-tooltip>
      </template>
    </v-data-table>

    <!-- DELETE EVENT -->
    <v-dialog
      v-model="show_delete_event"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="400"
    >
      <v-card>
        <v-card-title center class="headline justify-center">
          {{ $t("are_you_sure") }}
        </v-card-title>
        <v-card-text
          class="text-center justify-center"
          v-if="item_to_edit"
          v-html="$t('sure_delete_event', { event: item_to_edit.name })"
        >
        </v-card-text>
        <v-card-actions class="ps-5 pb-5 justify-center">
          <v-btn
            color="red"
            rounded
            depressed
            text
            class="pl-5 pr-5"
            @click="deleteEvent()"
          >
            {{ $t("delete") }}
          </v-btn>
          <v-btn
            color=""
            text
            rounded
            depressed
            class="pl-5 pr-5"
            @click="hideDeleteEvent"
          >
            {{ $t("cancel") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- MOVE EVENT -->
    <v-dialog
      v-model="show_move_event"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="400"
    >
      <v-card>
        <v-card-title center class="headline justify-center">
          {{ $t("move_calendar") }}
        </v-card-title>
        <v-card-text class="text-center justify-center" v-if="item_to_edit">
          <p v-html="$t('select_calendar_to_move')"></p>
          <v-row>
            <v-col cols="12" class="py-0">
              <v-select
                v-model="item_to_edit.calendar_id"
                :items="available_editable_calendars"
                item-text="name"
                item-value="id"
                :label="$t('target_calendar')"
                :rules="[v => !!v || $t('required')]"
                :placeholder="$t('select_calendar')"
                :disabled="item_to_edit.calendar_id === null"
                rounded
                outlined
                dense
              >
                <template v-slot:[`item`]="{ item }">
                  <span v-if="item.id !== 'new'">
                    {{ item.name }}
                  </span>
                  <b class="secondary--text" v-else>
                    {{ item.name }}
                  </b>
                </template>
              </v-select>
            </v-col>
            <v-col cols="12" class="py-0" v-if="must_show_new_calendar_name">
              <v-text-field
                v-model="item_to_edit.calendar_name"
                :label="$t('new_calendar_name')"
                :rules="[v => !!v || $t('required')]"
                :placeholder="$t('new_calendar_name')"
                maxlength="50"
                counter
                outlined
                rounded
                dense
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="ps-5 pb-5 justify-center">
          <v-btn
            color="primary"
            rounded
            depressed
            class="pl-5 pr-5"
            @click="moveEvent()"
            :disabled="!can_move_calendar"
          >
            {{ $t("move") }}
          </v-btn>
          <v-btn
            color=""
            text
            rounded
            depressed
            class="pl-5 pr-5"
            @click="hideMoveEvent"
          >
            {{ $t("cancel") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- CREATE/EDIT EVENT -->
    <v-dialog
      v-model="show_edit_event"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      :max-width="isRecurrentEvent ? 600 : 450"
    >
      <v-card>
        <v-card-title
          center
          class="headline justify-center py-6"
          v-if="item_to_edit.id"
        >
          {{ $t("edit_event") }}
        </v-card-title>
        <v-card-title center class="headline justify-center py-6" v-else>
          {{ $t("new_event") }}
        </v-card-title>

        <!-- Tabs para eventos recurrentes -->
        <v-tabs
          v-if="isRecurrentEvent && item_to_edit.id"
          v-model="edit_event_tab"
          grow
          class="mb-2"
        >
          <v-tab>{{ $t("event_info") }}</v-tab>
          <v-tab>{{ $t("other_editions") }}</v-tab>
        </v-tabs>

        <v-tabs-items v-model="edit_event_tab">
          <!-- Tab de información del evento -->
          <v-tab-item>
            <v-card-text class="text-center justify-center">
              <v-form ref="edit_event_form" v-model="can_set_calendar">
                <v-row>
                  <v-col cols="12" class="py-0">
                    <v-text-field
                      v-model="item_to_edit.name"
                      maxlength="50"
                      counter
                      :label="$t('name')"
                      :rules="[v => !!v || $t('required')]"
                      :placeholder="$t('name')"
                      outlined
                      rounded
                      dense
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="12" class="py-0">
                    <v-select
                      v-model="item_to_edit.type"
                      :items="calendar_store.types"
                      item-text="text"
                      item-value="value"
                      :label="$t('type')"
                      :rules="[v => !!v || $t('required')]"
                      :placeholder="$t('select_type')"
                      mandatory
                      rounded
                      outlined
                      dense
                      class=""
                    >
                      <template v-slot:[`item`]="{ item }"
                        ><v-icon small class="mr-1"
                          >{{ getEventTypeIcon(item.value) }}
                        </v-icon>
                        {{ item.text }}</template
                      >
                    </v-select>
                  </v-col>
                  <v-col cols="12" sm="12" class="py-0">
                    <v-select
                      v-model="item_to_edit.impact"
                      :items="calendar_store.impacts"
                      item-text="text"
                      item-value="value"
                      :label="$t('impact')"
                      :rules="[v => !!v || $t('required')]"
                      :placeholder="$t('select_impact')"
                      mandatory
                      rounded
                      outlined
                      dense
                      class=""
                    >
                    </v-select>
                  </v-col>
                  <v-col cols="12" sm="12" class="py-0">
                    <v-select
                      :disabled="item_to_edit.id === null"
                      v-model="item_to_edit.repeat"
                      :items="calendar_store.repeats"
                      item-text="text"
                      item-value="value"
                      :label="$t('repeat')"
                      :rules="[v => !!v || $t('required')]"
                      :placeholder="$t('select_repeat')"
                      mandatory
                      rounded
                      outlined
                      dense
                      class=""
                    >
                    </v-select>
                  </v-col>
                  <v-col
                    v-show="!calendar_id && !item_to_edit.id"
                    cols="12"
                    sm="12"
                    class="py-0"
                  >
                    <v-select
                      rounded
                      outlined
                      dense
                      v-model="item_to_edit.calendar_id"
                      :items="available_editable_calendars"
                      item-text="name"
                      item-value="id"
                      :label="$t('target_calendar')"
                      :rules="[v => !!v || $t('required')]"
                      :placeholder="$t('select')"
                    >
                      <template v-slot:[`item`]="{ item }">
                        <span v-if="item.id !== 'new'">
                          {{ item.name }}
                        </span>
                        <b class="secondary--text" v-else>
                          {{ item.name }}
                        </b>
                      </template>
                    </v-select>
                  </v-col>
                  <v-col
                    cols="12"
                    sm="12"
                    class="py-0"
                    v-if="must_show_new_calendar_name"
                  >
                    <v-text-field
                      v-model="item_to_edit.calendar_name"
                      maxlength="50"
                      counter
                      :label="$t('new_calendar_name')"
                      :rules="[v => !!v || $t('required')]"
                      :placeholder="$t('new_calendar_name')"
                      outlined
                      rounded
                      dense
                    ></v-text-field>
                  </v-col>
                  <v-col cols="10" class="py-0">
                    <v-menu :close-on-content-click="false" max-width="290">
                      <template v-slot:activator="{ on }">
                        <v-text-field
                          :value="formatedEventPeriod"
                          flat
                          readonly
                          dense
                          solo
                          rounded
                          outlined
                          background-color="white"
                          append-icon="mdi-calendar-month-outline"
                          v-on="on"
                          style="width:100%; flex: 0 0 auto;display: inline-flex; cursor:pointer"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="event_period"
                        :allowed-dates="eventPeriodAllowedDate"
                        range
                        first-day-of-week="1"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col cols="2" class="py-0">
                    <i
                      class="d-block subtitle-1 font-weight-bold pt-2 text-left"
                      >{{ total_days }} días</i
                    >
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
          </v-tab-item>

          <!-- Tab de otras ediciones -->
          <v-tab-item v-if="isRecurrentEvent && item_to_edit.id">
            <v-card-text>
              <v-data-table
                :headers="editions_headers"
                :items="event_editions"
                :loading="loading_editions"
                hide-default-footer
                disable-pagination
              >
                <template v-slot:[`item.year`]="{ item }">
                  {{ formatEditionYear(item) }}
                </template>
                <template v-slot:[`item.period`]="{ item }">
                  <v-btn
                    v-if="item.create"
                    color="secondary"
                    outlined
                    rounded
                    small
                    @click="showAddEdition(item)"
                  >
                    {{ $t("add") }}
                  </v-btn>
                  <v-menu
                    v-else
                    :close-on-content-click="false"
                    max-width="290"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-on="on"
                        :value="formatEditionPeriod(item)"
                        flat
                        readonly
                        dense
                        solo
                        rounded
                        outlined
                        hide-details
                        small
                        background-color="white"
                        append-icon="mdi-calendar-month-outline"
                        class="my-1"
                        style="width:100%; flex: 0 0 auto;display: inline-flex; cursor:pointer"
                        @click="
                          edition_period = [item.start_date, item.end_date]
                        "
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="edition_period"
                      :allowed-dates="eventPeriodAllowedDate"
                      range
                      first-day-of-week="1"
                      @change="updateEditionPeriod(item)"
                    ></v-date-picker>
                  </v-menu>
                </template>
                <template v-slot:[`item.impact`]="{ item }">
                  <!-- <v-chip
                    v-if="item.impact"
                    dark
                    small
                    :color="getImpactColor(item.impact)"
                  >
                    <b>{{ $t("impact_" + item.impact) }}</b>
                  </v-chip> -->
                  <v-select
                    v-if="item.impact"
                    v-model="item.impact"
                    :items="calendar_store.impacts"
                    item-text="text"
                    item-value="value"
                    :label="$t('impact')"
                    :placeholder="$t('select_impact')"
                    :color="getImpactColor(item.impact)"
                    class="my-2"
                    style="max-width: 170px;"
                    hide-details
                    rounded
                    outlined
                    dense
                    small
                    @change="updateEdition(item)"
                  ></v-select>
                </template>
              </v-data-table>
            </v-card-text>
          </v-tab-item>
        </v-tabs-items>

        <v-card-actions class="ps-5 pb-5 justify-center">
          <v-btn
            color="secondary"
            rounded
            depressed
            class="pl-5 pr-5"
            @click="setEvent()"
            :disabled="!can_set_calendar"
            v-if="item_to_edit.id"
          >
            {{ $t("set_event") }}
          </v-btn>
          <v-btn
            v-else
            color="secondary"
            rounded
            depressed
            class="pl-5 pr-5"
            @click="addEvent()"
            :disabled="!can_set_calendar"
          >
            {{ $t("add_event") }}
          </v-btn>
          <v-btn
            color=""
            text
            rounded
            depressed
            class="pl-5 pr-5"
            @click="hideEditEvent"
          >
            {{ $t("cancel") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- CONFIRMACIÓN PARA AÑADIR EDICIÓN -->
    <v-dialog
      v-model="show_confirm_add_edition"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="450"
    >
      <v-card>
        <v-card-title center class="headline justify-center">
          {{ $t("confirm_add_edition") }}
        </v-card-title>
        <v-card-text class="text-center justify-center" v-if="edition_to_add">
          <p
            v-html="
              $t('sure_add_edition', {
                year: formatEditionYear(edition_to_add)
              })
            "
          ></p>
        </v-card-text>
        <v-card-actions class="ps-5 pb-5 justify-center">
          <v-btn
            color="secondary"
            rounded
            depressed
            class="pl-5 pr-5"
            @click="confirmAddEdition()"
          >
            {{ $t("add") }}
          </v-btn>
          <v-btn
            color=""
            text
            rounded
            depressed
            class="pl-5 pr-5"
            @click="hideAddEdition"
          >
            {{ $t("cancel") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import * as Utils from "@/utils.js";
import Vue from "vue";
import { mapGetters, mapState } from "vuex";

export default {
  name: "CalendarEvents",
  data: () => ({
    image_source: "/img/header-2.jpg", //Mandatory path
    image_height: 380, //Mandatory number
    item_to_edit: {},
    event_period: [],
    edition_period: [],
    show_delete_event: false,
    show_move_event: false,
    show_edit_event: false,
    show_confirm_add_edition: false,
    edition_to_add: null,
    original_calendar_id: null,
    can_set_calendar: null,
    search: "",
    edit_event_tab: 0,
    loading_editions: false
  }),
  methods: {
    getDatatableRowClass() {
      return "a-datatable-row";
    },
    getEventTypeIcon(type) {
      return Utils.getEventTypeIcon(type);
    },
    getImpactColor(impact) {
      return this.calendar_store.impacts.find(i => i.value === impact).color;
    },
    isEventEditable(item) {
      return this.isAgency || (item.editable && !this.isAgency);
    },
    formatEditionYear(item) {
      return Vue.moment(item.start_date).format("YYYY");
    },
    formatEditionPeriod(item) {
      if (!item.start_date || !item.end_date) {
        return null;
      }
      return Utils.getFriendlyPeriod(item.start_date, item.end_date);
    },
    eventPeriodAllowedDate(date) {
      return true;
    },
    clearEditForm() {
      if (this.$refs.edit_event_form) {
        this.$refs.edit_event_form.resetValidation();
      }
    },
    clearEditData() {
      this.item_to_edit = {};
      this.event_period = [];
      this.edition_period = [];
      this.event_editions = [];
      this.getCalendars();
      this.getCalendarEvents();
    },
    showAddEvent() {
      this.clearEditForm();
      this.edit_event_tab = 0;
      this.show_edit_event = true;
      this.item_to_edit = {
        calendar_name: null,
        calendar_id: this.calendar_id ? this.calendar_id : null,
        hotel_id: this.calendar_id ? null : this.current_hotel.id
      };
      this.event_period = [
        Vue.moment().format("YYYY-MM-DD"),
        Vue.moment().format("YYYY-MM-DD")
      ];
      this.event_editions = [];
    },
    async showEditEvent(event) {
      if (!event) {
        return;
      }
      if (!this.isAgency && !event.editable) {
        this.$store.dispatch("setSnackbar", {
          title: this.$t("you_cannot_modify_event"),
          color: "error"
        });
        return;
      }
      this.clearEditForm();
      this.edit_event_tab = 0;
      this.show_edit_event = true;
      this.item_to_edit = JSON.parse(JSON.stringify(event));
      this.item_to_edit.calendar_name = null;
      this.item_to_edit.hotel_id = this.calendar_id
        ? null
        : this.current_hotel.id;
      this.event_period = [event.start_date, event.end_date];

      // Si el evento es recurrente, cargar las ediciones
      if (this.isRecurrentEvent) {
        await this.loadEventEditions();
      }
    },
    showDeleteEvent(event) {
      if (!event) {
        return;
      }
      if (!event.editable && !this.calendar_id) {
        this.$store.dispatch("setSnackbar", {
          title: this.$t("you_cannot_modify_event"),
          color: "error"
        });
        return;
      }
      this.show_delete_event = true;
      this.item_to_edit = event;
    },
    showMoveEvent(event) {
      if (!event) {
        return;
      }
      if (!event.editable && !this.calendar_id) {
        this.$store.dispatch("setSnackbar", {
          title: this.$t("you_cannot_modify_event"),
          color: "error"
        });
        return;
      }
      this.show_move_event = true;
      this.item_to_edit = JSON.parse(JSON.stringify(event));
      this.original_calendar_id = event.calendar_id;
      this.item_to_edit.hotel_id = this.calendar_id
        ? null
        : this.current_hotel.id; // Solo si estoy en la página de Calendario del hotel
    },
    showAddEdition(item) {
      this.edition_to_add = item;
      this.show_confirm_add_edition = true;
    },
    hideEditEvent() {
      this.show_edit_event = false;
      this.show_confirm_add_edition = false;
      this.edition_to_add = null;
      this.item_to_edit = {};
      this.event_editions = [];
    },
    hideDeleteEvent() {
      this.show_delete_event = false;
      this.item_to_edit = {};
    },
    hideMoveEvent() {
      this.show_move_event = false;
      this.original_calendar_id = null;
      this.item_to_edit = {};
    },
    hideAddEdition() {
      this.show_confirm_add_edition = false;
      this.edition_to_add = null;
    },
    async setEvent() {
      this.$store.dispatch("setSnackbar", {
        title: this.$t("modifying_event"),
        color: "secondary"
      });

      let sd = Vue.moment(this.event_period[0]);
      let ed = Vue.moment(this.event_period[1]);
      if (sd.isAfter(ed)) {
        this.item_to_edit.start_date = this.event_period[1];
        this.item_to_edit.end_date = this.event_period[0];
      } else {
        this.item_to_edit.start_date = this.event_period[0];
        this.item_to_edit.end_date = this.event_period[1];
      }

      this.item_to_edit.calendar_id =
        this.item_to_edit.calendar_id === "new"
          ? null
          : this.item_to_edit.calendar_id;

      this.$store
        .dispatch(this.setEventFunctionName, this.item_to_edit)
        .then(() => {
          this.$store.dispatch("setSnackbar", {
            title: this.$t("event_modified"),
            color: "accent"
          });
        })
        .catch(() => {
          Utils.showError(this, "event_not_modified");
        })
        .finally(() => {
          this.show_edit_event = false;
          this.clearEditData();
        });
    },
    addEvent() {
      this.$store.dispatch("setSnackbar", {
        title: this.$t("modifying_event"),
        color: "secondary"
      });

      let sd = Vue.moment(this.event_period[0]);
      let ed = Vue.moment(this.event_period[1]);
      if (sd.isAfter(ed)) {
        this.item_to_edit.start_date = this.event_period[1];
        this.item_to_edit.end_date = this.event_period[0];
      } else {
        this.item_to_edit.start_date = this.event_period[0];
        this.item_to_edit.end_date = this.event_period[1];
      }

      this.item_to_edit.calendar_id =
        this.item_to_edit.calendar_id === "new"
          ? null
          : this.item_to_edit.calendar_id;
      this.$store
        .dispatch(this.setEventFunctionName, this.item_to_edit)
        .then(() => {
          this.$store.dispatch("setSnackbar", {
            title: this.$t("event_modified"),
            color: "accent"
          });
        })
        .catch(() => {
          Utils.showError(this, "event_not_modified");
        })
        .finally(() => {
          this.show_edit_event = false;
          this.clearEditData();
        });
    },
    deleteEvent() {
      this.$store.dispatch("setSnackbar", {
        title: this.$t("deleting_event"),
        color: "secondary"
      });

      this.$store
        .dispatch("deleteEvent", { id: this.item_to_edit.id })
        .then(() => {
          this.show_delete_event = false;
          this.$store.dispatch("setSnackbar", {
            title: this.$t("event_deleted"),
            color: "accent"
          });
        })
        .catch(() => {
          Utils.showError(this, "event_not_deleted");
        })
        .finally(() => {
          this.show_delete_event = false;
          this.clearEditData();
        });
    },
    moveEvent() {
      this.$store.dispatch("setSnackbar", {
        title: this.$t("moving_event"),
        color: "secondary"
      });

      this.show_move_event = false;
      this.original_calendar_id = null;
      this.item_to_edit.calendar_id =
        this.item_to_edit.calendar_id === "new"
          ? null
          : this.item_to_edit.calendar_id;
      this.$store
        .dispatch(this.setEventFunctionName, this.item_to_edit)
        .then(() => {
          this.$store.dispatch("setSnackbar", {
            title: this.$t("event_moved"),
            color: "accent"
          });
        })
        .catch(() => {
          Utils.showError(this, "event_not_moved");
        })
        .finally(() => {
          this.clearEditData();
        });
    },
    confirmAddEdition() {
      if (!this.edition_to_add) return;

      this.$store.dispatch("setSnackbar", {
        title: this.$t("adding_edition"),
        color: "secondary"
      });

      this.$store
        .dispatch("createEventEditionInYear", {
          recurrent_id: this.item_to_edit.recurrent_id,
          year: Vue.moment(this.edition_to_add.start_date).format("YYYY")
        })
        .then(async () => {
          this.$store.dispatch("setSnackbar", {
            title: this.$t("edition_added"),
            color: "accent"
          });
          await this.loadEventEditions();
        })
        .catch(() => {
          Utils.showError(this, "error_adding_edition");
        })
        .finally(() => {
          this.show_confirm_add_edition = false;
          this.edition_to_add = null;
        });
    },
    updateEditionPeriod(item) {
      item.start_date = this.edition_period[0];
      item.end_date = this.edition_period[1];
      this.updateEdition(item);
    },
    updateEdition(item) {
      this.$store.dispatch("setSnackbar", {
        title: this.$t("modifying_event"),
        color: "secondary"
      });

      this.$store
        .dispatch("setCalendarRecurrentEvent", item)
        .then(async () => {
          this.$store.dispatch("setSnackbar", {
            title: this.$t("event_modified"),
            color: "accent"
          });
          await this.loadEventEditions();
        })
        .catch(() => {
          Utils.showError(this, "event_not_modified");
        });
    },
    async loadEventEditions() {
      this.loading_editions = true;
      let oldestStartDate = null;
      let newestStartDate = null;
      try {
        if (this.item_to_edit.recurrent_id) {
          const response = await this.$store.dispatch("getEventEditions", {
            recurrent_id: this.item_to_edit.recurrent_id
          });

          this.event_editions = this.event_editions.map(edition => {
            edition.period = [edition.start_date, edition.end_date];
            return edition;
          });

          // Buscamos la fecha de inicio mas antigua
          oldestStartDate = this.event_editions.reduce((oldest, current) => {
            return Vue.moment(current.start_date).isBefore(
              Vue.moment(oldest.start_date)
            )
              ? current
              : oldest;
          }, this.event_editions[0]);

          // Buscamos la fecha de inicio mas nueva
          newestStartDate = this.event_editions.reduce((newest, current) => {
            return Vue.moment(current.start_date).isAfter(
              Vue.moment(newest.start_date)
            )
              ? current
              : newest;
          }, this.event_editions[0]);
        } else {
          this.event_editions = [];

          // Añdimos año anterior
          oldestStartDate = Vue.moment(this.item_to_edit.start_date)
            .subtract(1, "year")
            .format("YYYY-MM-DD");
          newestStartDate = Vue.moment(this.item_to_edit.start_date)
            .subtract(1, "year")
            .format("YYYY-MM-DD");
        }

        let diffrence = 1;
        if (this.item_to_edit.repeat === "biannual") {
          diffrence = 2;
        }

        // Añdimos año anterior
        this.event_editions.push({
          start_date: Vue.moment(oldestStartDate.start_date)
            .subtract(diffrence, "year")
            .format("YYYY-MM-DD"),
          end_date: Vue.moment(oldestStartDate.end_date)
            .subtract(diffrence, "year")
            .format("YYYY-MM-DD"),
          create: true
        });

        // Añdimos año siguiente
        this.event_editions.push({
          start_date: Vue.moment(newestStartDate.start_date)
            .add(diffrence, "year")
            .format("YYYY-MM-DD"),
          end_date: Vue.moment(newestStartDate.end_date)
            .add(diffrence, "year")
            .format("YYYY-MM-DD"),
          create: true
        });

        // Ordenar por año
        this.event_editions.sort((a, b) =>
          Vue.moment(a.start_date).diff(Vue.moment(b.start_date))
        );
      } catch (error) {
        Utils.showError(this, "error_loading_editions");
      } finally {
        this.loading_editions = false;
      }
    },
    getCalendars() {
      this.$store.dispatch("getCalendars", {}).catch(() => {
        Utils.showError(this, "something_went_wrong");
      });
    },
    getCalendarEvents() {
      if (
        !this.calendar_store.calendar_selected ||
        this.calendar_store.calendar_selected.id == null
      ) {
        return;
      }
      this.$store
        .dispatch("getCalendarEvents", {
          year: this.calendar_store.calendar_year,
          calendar_id: this.calendar_store.calendar_selected.id
        })
        .catch(() => {
          Utils.showError(this, "something_went_wrong");
        });
    }
  },
  computed: {
    calendar_id() {
      return this.calendar_store.calendar_selected &&
        this.calendar_store.calendar_selected.id
        ? this.calendar_store.calendar_selected.id
        : null;
    },
    isAgency() {
      return (
        this.current_user && this.current_user.permissions.eventsmanagement
      );
    },
    isCalendarEditable() {
      return (
        this.isAgency ||
        (this.calendar_store.calendar_selected &&
          this.calendar_store.calendar_selected.editable)
      );
    },
    isRecurrentEvent() {
      return (
        this.item_to_edit.repeat === "annual" ||
        this.item_to_edit.repeat === "biannual"
      );
    },
    available_calendars() {
      let calendars = JSON.parse(JSON.stringify(this.getHotelCalendars));
      calendars.push({ id: "new", name: this.$t("new_calendar") });
      return calendars;
    },
    available_editable_calendars() {
      if (this.isAgency) {
        return this.available_calendars;
      }
      return this.available_calendars.filter(c => c.editable || c.id === "new");
    },
    no_calendar_events_text() {
      let text = this.$t("no_calendar_events");
      if (this.calendar_store.calendar_selected.events_count > 0) {
        text = this.$t("no_calendar_events_this_year");
      }
      return text;
    },
    setEventFunctionName() {
      return this.calendar_id ? "setCalendarEvent" : "setHotelEvent";
    },
    filteredEvents() {
      let events = this.calendar_store.calendar_events;
      if (this.search && this.search.length > 0) {
        events = events.filter(e =>
          e.name.toLowerCase().includes(this.search.toLowerCase())
        );
      }
      return this.extendEvents(events);
    },
    must_show_new_calendar_name() {
      return this.item_to_edit.calendar_id === "new";
    },
    total_days() {
      return Utils.getDuration(this.event_period[0], this.event_period[1]);
    },
    formatedEventPeriod() {
      return Utils.getFriendlyPeriod(
        this.event_period[0],
        this.event_period[1]
      );
    },
    can_move_calendar() {
      return this.item_to_edit.calendar_id !== this.original_calendar_id;
    },
    hotel_events_headers() {
      return [
        {
          text: this.$t("event"),
          align: "left",
          sortable: true,
          value: "name"
        },
        {
          text: this.$t("dates"),
          align: "start",
          sortable: true,
          value: "start_date",
          width: "220px"
        },
        {
          text: this.$t("calendar"),
          align: "start",
          sortable: true,
          value: "original_calendar_name"
        },
        {
          text: this.$t("impact"),
          align: "center",
          sortable: true,
          value: "impact"
        },
        {
          text: this.$t("repeat"),
          align: "center",
          sortable: true,
          value: "repeat"
        },
        {
          text: null,
          align: "center",
          sortable: false,
          value: "actions",
          width: "120px"
        }
      ];
    },
    editions_headers() {
      return [
        {
          text: this.$t("edition"),
          align: "start",
          sortable: true,
          value: "year"
        },
        {
          text: this.$t("period"),
          align: "start",
          sortable: true,
          value: "period"
        },
        {
          text: this.$t("impact"),
          align: "center",
          sortable: true,
          value: "impact"
        }
      ];
    },
    event_editions: {
      get() {
        return this.calendar_store.event_editions;
      },
      set(value) {
        this.$store.dispatch("setEventEditions", value);
      }
    },
    ...mapState({
      calendar_store: state => state.calendar
    }),
    ...mapGetters({
      current_user: "current_user",
      current_hotel: "current_hotel",
      extendEvents: "extendEvents",
      getHotelCalendars: "getHotelCalendars"
    })
  }
};
</script>
